import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, shareApiPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRightCircle, Edit2, History, FileDown, Share2, Search } from "lucide-react";
import { usePagedList, PaginationBar } from "@/components/Pagination";
import { Input } from "@/components/ui/input";

const ESTADO_OPTS = [
  { v: "todos", l: "Todos los estados" },
  { v: "borrador", l: "Borrador" },
  { v: "convertida", l: "Convertida" },
];

export default function Quotations() {
  const [items, setItems] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [filterEstado, setFilterEstado] = useState("todos");
  const [q, setQ] = useState("");  // ITER189: barra de búsqueda
  const filtered = useMemo(() => {
    let res = items;
    if (filterEstado !== "todos") res = res.filter(it => (it.estado || "borrador") === filterEstado);
    const term = q.trim().toLowerCase();
    if (term) {
      res = res.filter(it =>
        (it.numero || "").toLowerCase().includes(term) ||
        (it.cliente_nombre || "").toLowerCase().includes(term) ||
        (it.cliente_razon_social || "").toLowerCase().includes(term) ||
        (it.cliente_nombre_comercial || "").toLowerCase().includes(term)
      );
    }
    return res;
  }, [items, filterEstado, q]);
  const pagedQuotations = usePagedList(filtered, 20);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/sales/cotizaciones");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const editar = (it) => {
    if (it.estado === "convertida") {
      return toast.error("No se puede editar una cotización convertida");
    }
    navigate(`/cotizaciones/${it.id}/editar`);
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar ${it.numero}?`)) return;
    try { await api.delete(`/sales/cotizaciones/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const convertir = async (it) => {
    if (!window.confirm(`¿Convertir ${it.numero} en Nota de Pedido?\nLas líneas con stock parcial se reducirán y los faltantes irán a una Solicitud de Compra para Gerencia.`)) return;
    try {
      const { data } = await api.patch(`/sales/cotizaciones/${it.id}/convertir-pedido`);
      const ped = data?.pedido?.numero;
      const sol = data?.solicitud_compra?.numero;
      if (ped && sol) toast.success(`Pedido ${ped} creado · Solicitud ${sol} pendiente de Gerencia`);
      else if (ped) toast.success(`Pedido ${ped} creado`);
      else if (sol) toast.success(`Solicitud de compra ${sol} creada (sin stock disponible)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (it) => {
    try {
      const r = await api.get(`/sales/cotizaciones/${it.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cotizacion_${it.numero}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sharePdf = async (it) => {
    try {
      const r = await shareApiPdf(`/sales/cotizaciones/${it.id}/pdf`, {
        filename: `Cotizacion_${it.numero}.pdf`,
        title: `Cotización ${it.numero}`,
        text: `Adjunto cotización ${it.numero} por $${(it.total || 0).toFixed(2)}.`,
      });
      if (r.fallback === "downloaded") toast.message("PDF descargado · Adjúntelo manualmente");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Ventas</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Cotizaciones</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Las cotizaciones permiten stock 0. Al convertir a Nota de Pedido sí se valida stock disponible.</p>
        </div>
        <Button onClick={() => navigate("/cotizaciones/nueva")} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-quote-btn">
          <Plus size={16} className="mr-2" /> Nueva cotización
        </Button>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <label className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold block mb-1">Estado</label>
          <Select value={filterEstado} onValueChange={setFilterEstado}>
            <SelectTrigger data-testid="quote-filter-estado" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ESTADO_OPTS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[260px] flex-1">
          <label className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold block mb-1">Buscar</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-[#8ba397]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Número, cliente (razón social o nombre comercial)…"
              className="h-9 pl-8"
              data-testid="quote-search-input"
            />
          </div>
        </div>
        <div className="text-xs text-[#5c7368] ml-auto">
          Mostrando <b>{filtered.length}</b> de {items.length} cotizaciones
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-x-auto">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Vendedor</th><th className="text-right">Subtotal</th><th className="text-right">IVA</th><th className="text-right">Total</th><th>Estado</th><th className="w-44"></th></tr></thead>
          <tbody>
            {pagedQuotations.paged.map(it => (
              <tr key={it.id} data-testid={`quote-row-${it.id}`}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td className="font-medium">{it.cliente_nombre}</td>
                <td className="text-[#4a5c53]">{it.vendedor_nombre}</td>
                <td className="text-right tabular-nums">${(it.subtotal ?? Math.max(0, (it.total || 0) - (it.iva_total || 0))).toFixed(2)}</td>
                <td className="text-right tabular-nums text-[#5c7368]">${(it.iva_total || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">${(it.total || 0).toFixed(2)}</td>
                <td><Badge variant={it.estado === "convertida" ? "secondary" : "outline"} className="capitalize">{it.estado}</Badge></td>
                <td>
                  <div className="flex gap-1 justify-end">
                    {(it.edit_history || []).length > 0 && (
                      <Button size="icon" variant="ghost" onClick={() => setHistoryOpen(it)} title="Ver historial" data-testid={`quote-history-${it.id}`}>
                        <History size={14} className="text-[#205c45]" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => downloadPdf(it)} title="Descargar PDF" data-testid={`quote-pdf-${it.id}`}>
                      <FileDown size={14} className="text-[#205c45]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => sharePdf(it)} title="Compartir PDF" data-testid={`quote-share-${it.id}`}>
                      <Share2 size={14} className="text-[#205c45]" />
                    </Button>
                    {it.estado !== "convertida" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => editar(it)} data-testid={`quote-edit-${it.id}`}>
                          <Edit2 size={14} className="mr-1" /> Editar
                        </Button>
                        <Button size="sm" variant="outline" className="text-[#205c45]" onClick={() => convertir(it)} data-testid={`convert-${it.id}`}>
                          <ArrowRightCircle size={14} className="mr-1" /> A Pedido
                        </Button>
                      </>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => remove(it)} title="Eliminar" data-testid={`quote-delete-${it.id}`}>
                      <Trash2 size={16} className="text-[#b53a25]" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8ba397]">
              {items.length === 0 ? "Sin cotizaciones aún" : `Sin cotizaciones en estado "${filterEstado}"`}
            </td></tr>}
          </tbody>
        </table>
        <PaginationBar page={pagedQuotations.page} total={pagedQuotations.total} pageSize={pagedQuotations.pageSize} onPrev={pagedQuotations.prev} onNext={pagedQuotations.next} label="cotizaciones" />
      </div>

      <Dialog open={!!historyOpen} onOpenChange={(v) => { if (!v) setHistoryOpen(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle><History className="inline mr-2" size={18} />Historial de edición · {historyOpen?.numero}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(historyOpen?.edit_history || []).length === 0 && <p className="text-sm text-[#5c7368]">No hay ediciones registradas.</p>}
            {(historyOpen?.edit_history || []).map((h, idx) => (
              <div key={`${h.fecha || idx}-${h.user_id || h.user_nombre || idx}`} className="border-l-2 border-[#205c45] pl-3 py-2 bg-[#f7f9f8] rounded">
                <div className="text-xs text-[#5c7368]">{(h.fecha || "").slice(0, 16).replace("T", " ")} · <b>{h.user_nombre}</b></div>
                <ul className="text-sm mt-1 space-y-0.5 list-disc pl-5">
                  {(h.cambios || []).map((c, i) => <li key={`${idx}-${i}-${(c || "").slice(0,12)}`}>{c}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
